import React from "react";

import { PageLoadingIcon } from "@packages/ds";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { useOrganizationCarbonEmissions } from "Reducers/carbonEmissions/hooks/useOrganizationCarbonEmissions";

import { Aside } from "./Aside";
import { EmissionsPerProjectTable } from "./EmissionsPerProjectTable";
import { GraphSection } from "./GraphSection";

const Page = () => {
  const intl = useIntl();
  const { organizationId } = useParams<{ organizationId: string }>();

  const { isLoading, previousYearTotal, monthlyChartData, projectsTableData } =
    useOrganizationCarbonEmissions(organizationId);

  // Compute years for display
  const lastYear = new Date().getFullYear() - 1;

  // Create default empty chart data to avoid undefined error
  const safeGraphData = monthlyChartData ?? [];

  return (
    <PageWrapper>
      <Aside />
      <MainContent>
        <MainHeading>
          {intl.formatMessage({
            id: "carbon.emissions.main.title"
          })}
        </MainHeading>
        <MainNote>
          {intl.formatMessage({
            id: "carbon.emissions.main.note"
          })}
        </MainNote>
        <EmissionsCard>
          <GraphSection
            graphData={safeGraphData}
            year={lastYear}
            total={previousYearTotal}
          />
          <EmissionsDivider />
          <SectionHeading>
            {intl.formatMessage(
              {
                id: "carbon.emissions.projects.title"
              },
              { year: lastYear }
            )}
          </SectionHeading>

          {isLoading ? (
            <PageLoadingIcon />
          ) : (
            <EmissionsPerProjectTable
              projectsTableData={projectsTableData}
              year={lastYear}
            />
          )}
        </EmissionsCard>
      </MainContent>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  --aside-width: 356px;
  --content-padding: 40px;
  --column-gap: 32px;

  display: grid;
  grid-template-columns: var(--aside-width) 1fr;
  min-height: 100vh;
  gap: var(--column-gap);
  margin-left: calc(-1 * var(--content-padding));
`;

const MainContent = styled.main`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 40px;
`;

const MainHeading = styled.h2`
  color: var(--mode-text-neutral-default);
  font-size: var(--fs-xxl);
  font-weight: 700;
`;

const MainNote = styled.p`
  margin-top: -16px;
  color: var(--mode-text-neutral-default-weak);
  font-family: var(--ff-sans, "Open Sans");
  font-size: var(14px);
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`;

const EmissionsCard = styled.section`
  background: var(--mode-bg-neutral-weakest);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
`;

const EmissionsDivider = styled.hr`
  border: 0;
  border-top: 1px var(--mode-border-neutral-weakest);
  margin: 24px 0;
`;

const SectionHeading = styled.h2`
  font-size: 15px;
  margin-top: 0;
  margin-bottom: 24px;
`;

export default Page;
